import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'cash_flow_data';

export const CashFlowCalculator = () => {
  const [openingBalance, setOpeningBalance] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).openingBalance : 100000;
  });
  
  const [monthlyInflows, setMonthlyInflows] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyInflows : 50000;
  });
  
  const [monthlyOutflows, setMonthlyOutflows] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyOutflows : 45000;
  });
  
  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timePeriod : 12;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      openingBalance,
      monthlyInflows,
      monthlyOutflows,
      timePeriod
    }));
  }, [openingBalance, monthlyInflows, monthlyOutflows, timePeriod]);

  const calculateCashFlow = () => {
    const opening = parseFloat(openingBalance) || 0;
    const inflows = parseFloat(monthlyInflows) || 0;
    const outflows = parseFloat(monthlyOutflows) || 0;
    const periods = parseFloat(timePeriod) || 0;

    const netCashFlow = inflows - outflows;
    const totalInflows = inflows * periods;
    const totalOutflows = outflows * periods;
    const endingBalance = opening + (netCashFlow * periods);
    const burnRate = netCashFlow < 0 ? Math.abs(outflows) : 0;
    const cashRunway = netCashFlow < 0 && burnRate > 0 ? opening / burnRate : 0;

    return {
      netCashFlow,
      endingBalance,
      burnRate,
      cashRunway: Math.floor(cashRunway),
      totalInflows,
      totalOutflows,
      cumulativeCashFlow: netCashFlow * periods
    };
  };

  const generateMonthlyCashFlow = () => {
    const opening = parseFloat(openingBalance) || 0;
    const inflows = parseFloat(monthlyInflows) || 0;
    const outflows = parseFloat(monthlyOutflows) || 0;
    const periods = parseFloat(timePeriod) || 0;

    const data = [];
    let balance = opening;
    let cumulative = 0;

    data.push({
      month: 0,
      inflows: 0,
      outflows: 0,
      netCashFlow: 0,
      balance: Math.round(balance),
      cumulative: 0
    });

    for (let month = 1; month <= periods; month++) {
      const netFlow = inflows - outflows;
      balance += netFlow;
      cumulative += netFlow;
      
      data.push({
        month: month,
        inflows: Math.round(inflows),
        outflows: Math.round(outflows),
        netCashFlow: Math.round(netFlow),
        balance: Math.round(balance),
        cumulative: Math.round(cumulative)
      });
    }
    return data;
  };

  const generateScenarioAnalysis = () => {
    const opening = parseFloat(openingBalance) || 0;
    const inflows = parseFloat(monthlyInflows) || 0;
    const outflows = parseFloat(monthlyOutflows) || 0;

    const scenarios = [
      { name: 'Worst Case', inflowMultiplier: 0.7, outflowMultiplier: 1.3 },
      { name: 'Conservative', inflowMultiplier: 0.85, outflowMultiplier: 1.15 },
      { name: 'Base Case', inflowMultiplier: 1, outflowMultiplier: 1 },
      { name: 'Optimistic', inflowMultiplier: 1.15, outflowMultiplier: 0.9 },
      { name: 'Best Case', inflowMultiplier: 1.3, outflowMultiplier: 0.8 }
    ];

    return scenarios.map(scenario => {
      const adjustedInflows = inflows * scenario.inflowMultiplier;
      const adjustedOutflows = outflows * scenario.outflowMultiplier;
      const netFlow = adjustedInflows - adjustedOutflows;
      const endingBalance = opening + (netFlow * 12);
      
      return {
        name: scenario.name,
        netCashFlow: Math.round(netFlow),
        endingBalance: Math.round(endingBalance)
      };
    });
  };

  const generateInflowOutflowBreakdown = () => {
    const inflows = parseFloat(monthlyInflows) || 0;
    const outflows = parseFloat(monthlyOutflows) || 0;
    const periods = parseFloat(timePeriod) || 0;

    return [
      { name: 'Total Inflows', value: Math.round(inflows * periods) },
      { name: 'Total Outflows', value: Math.round(outflows * periods) }
    ];
  };

  const generateCashRunwayData = () => {
    const opening = parseFloat(openingBalance) || 0;
    const inflows = parseFloat(monthlyInflows) || 0;
    const outflows = parseFloat(monthlyOutflows) || 0;

    if (inflows >= outflows) {
      return []; // Positive cash flow, no runway concern
    }

    const data = [];
    let balance = opening;
    const burnRate = outflows - inflows;
    let month = 0;

    while (balance > 0 && month <= 24) {
      data.push({
        month: month,
        balance: Math.round(balance)
      });
      balance -= burnRate;
      month++;
    }

    return data;
  };

  const results = calculateCashFlow();
  const monthlyCashFlow = generateMonthlyCashFlow();
  const scenarioAnalysis = generateScenarioAnalysis();
  const inflowOutflowBreakdown = generateInflowOutflowBreakdown();
  const cashRunwayData = generateCashRunwayData();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Cash Flow Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Cash Flow Analysis?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Cash flow analysis tracks the movement of money in and out of your business. Understanding your cash position 
                  is critical for survival and growth. This calculator helps you monitor cash balance, burn rate, and runway.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your cash flow details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="openingBalance" className="text-sm font-medium">
                      Opening Cash Balance ($)
                    </Label>
                    <Input
                      id="openingBalance"
                      type="number"
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Starting cash in bank</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthlyInflows" className="text-sm font-medium">
                      Monthly Cash Inflows ($)
                    </Label>
                    <Input
                      id="monthlyInflows"
                      type="number"
                      value={monthlyInflows}
                      onChange={(e) => setMonthlyInflows(e.target.value)}
                      placeholder="50000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Revenue, investments, loans</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthlyOutflows" className="text-sm font-medium">
                      Monthly Cash Outflows ($)
                    </Label>
                    <Input
                      id="monthlyOutflows"
                      type="number"
                      value={monthlyOutflows}
                      onChange={(e) => setMonthlyOutflows(e.target.value)}
                      placeholder="45000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Expenses, payroll, suppliers</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timePeriod" className="text-sm font-medium">
                      Time Period (Months)
                    </Label>
                    <Input
                      id="timePeriod"
                      type="number"
                      value={timePeriod}
                      onChange={(e) => setTimePeriod(e.target.value)}
                      placeholder="12"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full bg-accent text-white hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setOpeningBalance(openingBalance)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Cash Flow Results</CardTitle>
                    <CardDescription>Your liquidity analysis</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.netCashFlow >= 0 ? 'from-secondary-light to-card border-secondary/20' : 'from-destructive/10 to-card border-destructive/20'} border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.netCashFlow >= 0 ? 'bg-secondary' : 'bg-destructive'} flex items-center justify-center`}>
                            {results.netCashFlow >= 0 ? <TrendingUp className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Cash Flow (Monthly)</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.netCashFlow >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                          {formatCurrency(results.netCashFlow)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Ending Balance</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.endingBalance)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">after {timePeriod} months</p>
                      </div>

                      {results.burnRate > 0 && (
                        <>
                          <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-white" />
                              </div>
                              <p className="text-sm font-medium text-muted-foreground">Burn Rate</p>
                            </div>
                            <p className="text-3xl font-display font-bold text-destructive">
                              {formatCurrency(results.burnRate)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">per month</p>
                          </div>

                          <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                                <Calculator className="w-5 h-5 text-white" />
                              </div>
                              <p className="text-sm font-medium text-muted-foreground">Cash Runway</p>
                            </div>
                            <p className="text-3xl font-display font-bold text-accent">
                              {results.cashRunway} months
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">before cash runs out</p>
                          </div>
                        </>
                      )}
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Total Inflows</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalInflows)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            over {timePeriod} months
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Total Outflows</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalOutflows)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            over {timePeriod} months
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Monthly Cash Flow Breakdown</CardTitle>
                    <CardDescription>
                      Month-by-month cash position
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 font-semibold text-foreground">Month</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Inflows</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Outflows</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Net Flow</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Cash Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyCashFlow.slice(0, 13).map((row, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                            >
                              <td className="py-3 px-4 font-medium">{row.month}</td>
                              <td className="py-3 px-4 text-right text-secondary">{formatCurrency(row.inflows)}</td>
                              <td className="py-3 px-4 text-right text-destructive">{formatCurrency(row.outflows)}</td>
                              <td className={`py-3 px-4 text-right font-semibold ${row.netCashFlow >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                                {formatCurrency(row.netCashFlow)}
                              </td>
                              <td className="py-3 px-4 text-right font-semibold text-primary">{formatCurrency(row.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Cash Balance Over Time</CardTitle>
                <CardDescription className="text-base">
                  Track your cash position month by month
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyCashFlow}>
                      <defs>
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Cash Balance ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="balance" 
                        stroke="hsl(var(--primary))" 
                        fill="url(#colorBalance)"
                        strokeWidth={3}
                        name="Cash Balance"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Inflows vs Outflows</CardTitle>
                  <CardDescription>
                    Monthly cash movement comparison
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyCashFlow.slice(1, 13)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="inflows" fill="hsl(var(--secondary))" name="Cash Inflows" />
                        <Bar dataKey="outflows" fill="hsl(var(--destructive))" name="Cash Outflows" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Scenario Analysis</CardTitle>
                  <CardDescription>
                    Ending balance under different scenarios
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioAnalysis}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="endingBalance" name="Ending Balance">
                          {scenarioAnalysis.map((entry, index) => (
                            <Bar 
                              key={`bar-${index}`} 
                              fill={entry.endingBalance >= parseFloat(openingBalance) ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {results.burnRate > 0 && cashRunwayData.length > 0 && (
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display text-destructive">Cash Runway Projection</CardTitle>
                  <CardDescription className="text-base">
                    How long until cash runs out at current burn rate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={cashRunwayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          label={{ value: 'Cash Balance ($)', angle: -90, position: 'insideLeft' }}
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="balance" 
                          stroke="hsl(var(--destructive))" 
                          strokeWidth={3}
                          name="Cash Balance"
                          dot={{ fill: 'hsl(var(--destructive))' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Cash Flow</CardTitle>
                <CardDescription className="text-base">
                  Learn how to interpret and improve your cash position
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Your Cash Flow Situation
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your net monthly cash flow is <strong className={results.netCashFlow >= 0 ? 'text-secondary' : 'text-destructive'}>
                      {formatCurrency(results.netCashFlow)}
                    </strong>. Starting with {formatCurrency(parseFloat(openingBalance))}, 
                    your ending balance after {timePeriod} months will be <strong className="text-primary">{formatCurrency(results.endingBalance)}</strong>.
                  </p>
                  {results.burnRate > 0 ? (
                    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <p className="text-sm font-semibold text-destructive mb-2">⚠️ Negative Cash Flow Alert</p>
                      <p className="text-sm text-muted-foreground">
                        You're burning <strong>{formatCurrency(results.burnRate)}</strong> per month. 
                        At this rate, you have approximately <strong>{results.cashRunway} months</strong> of runway before cash runs out. 
                        Urgent action needed to improve cash flow or secure funding.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                      <p className="text-sm font-semibold text-secondary mb-2">✅ Positive Cash Flow</p>
                      <p className="text-sm text-muted-foreground">
                        Great news! You're generating <strong>{formatCurrency(results.netCashFlow)}</strong> in positive cash flow each month. 
                        Your cash position is healthy and growing.
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Cash Flow vs Profit: The Critical Difference
                  </h3>
                  <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">💰 Cash Flow (What This Calculator Shows)</h4>
                      <p className="mb-2">
                        <strong>Definition:</strong> Actual money moving in and out of your bank account
                      </p>
                      <p className="mb-2">
                        <strong>Why It Matters:</strong> You need cash to pay bills, salaries, and suppliers. Profitable companies can fail due to poor cash management.
                      </p>
                      <p>
                        <strong>Key Point:</strong> Revenue booked doesn't equal cash received. Accounts receivable are sales, not cash.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📊 Profit (Different Concept)</h4>
                      <p className="mb-2">
                        <strong>Definition:</strong> Revenue minus expenses on an accrual basis
                      </p>
                      <p className="mb-2">
                        <strong>Why It Matters:</strong> Shows business viability and long-term health
                      </p>
                      <p>
                        <strong>Key Point:</strong> Can be profitable on paper but have no cash (and vice versa)
                      </p>
                    </div>
                    <p className="mt-4 font-semibold text-foreground">
                      Common Scenario: A growing company books $100k revenue (profit!) but customers pay in 60 days. 
                      Meanwhile, you must pay $80k in expenses immediately. Result: Profitable but negative cash flow = crisis.
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Understanding Burn Rate and Cash Runway
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    {results.burnRate > 0 ? (
                      <>
                        Your burn rate of <strong className="text-destructive">{formatCurrency(results.burnRate)}</strong>/month means 
                        you're spending more than you're earning. With {formatCurrency(parseFloat(openingBalance))} in the bank, 
                        you have <strong>{results.cashRunway} months</strong> of runway.
                      </>
                    ) : (
                      <>
                        You don't have a burn rate concern since your inflows exceed outflows. This is the ideal situation for sustainable growth.
                      </>
                    )}
                  </p>
                  {results.burnRate > 0 && (
                    <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Runway Alert Level:</p>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          results.cashRunway > 12 ? 'bg-secondary/20 text-secondary' :
                          results.cashRunway > 6 ? 'bg-accent/20 text-accent' :
                          'bg-destructive/20 text-destructive'
                        }`}>
                          {results.cashRunway > 12 ? 'Healthy' :
                           results.cashRunway > 6 ? 'Monitor Closely' :
                           'Critical'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {results.cashRunway > 12 ? 
                          'Good runway. Focus on sustainable growth and efficiency improvements.' :
                         results.cashRunway > 6 ?
                          'Moderate concern. Start planning for funding or cost reductions within 3 months.' :
                          'Critical situation. Immediate action required: cut costs, accelerate revenue, or raise capital.'}
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Key Cash Flow Risks
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Timing Mismatch:</strong> Revenue is recognized when invoiced, 
                        but cash comes later. Pay attention to Days Sales Outstanding (DSO).
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Seasonal Variations:</strong> Many businesses have irregular 
                        cash flows. Plan for lean periods with cash reserves.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Growth Paradox:</strong> Rapid growth often strains cash as you 
                        invest in inventory, staff, and infrastructure before revenue comes in.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Fixed Costs:</strong> Rent, salaries, and subscriptions must be 
                        paid regardless of revenue. High fixed costs increase burn rate risk.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Strategies to Improve Cash Flow
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Accelerate Inflows</h4>
                      <p className="text-muted-foreground">
                        Offer early payment discounts, require deposits, invoice immediately, accept multiple payment methods, 
                        and follow up on overdue accounts aggressively.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Delay Outflows (Responsibly)</h4>
                      <p className="text-muted-foreground">
                        Negotiate better payment terms with suppliers, use vendor financing when available, 
                        and time discretionary expenses strategically.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Reduce Operating Costs</h4>
                      <p className="text-muted-foreground">
                        Review all subscriptions and recurring costs, negotiate contracts, outsource non-core functions, 
                        and eliminate wasteful spending.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Build a Cash Reserve</h4>
                      <p className="text-muted-foreground">
                        Aim for 3-6 months of operating expenses in reserve. This cushion provides peace of mind and 
                        flexibility during lean periods.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CashFlowCalculator;
